import { Image as ImageIcon, Video as VideoIcon } from "lucide-react";
import { creativeTools, creativeMenuCategories, creativeToolPath } from "@shared/creativeTools";
/* The v2 landing's header: the same destinations as Landing.tsx's HomeNav
   (tools, the creators, library, pricing, blog; and for signed-in users
   credits, upgrade, rewards, notifications and the account menu), in the
   design's typography. The theme button keeps id="theme" — LandingV2's
   initLanding binds to it. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { trpc } from "@/lib/trpc";
import { getPlanTierIndex } from "@/lib/pricingPlans";
import UpgradeModal, { type UpgradeModalTab } from "@/components/UpgradeModal";
import { DISCORD_INVITE_URL } from "@/const";
import RewardsBanner from "@/components/rewards/RewardsBanner";
import InviteFriendsDialog from "@/components/InviteFriendsDialog";
import { languageNames } from "@/locales";

type Props = {
  onLogin: () => void;
  onJoin: () => void;
  onThemeToggle?: () => void;
};

// Labels come from the shared globalNav namespace (already translated).
const TOOLS = [
  { key: "globalNav.tools.aiMusic", href: "/ai-music-generator" },
  { key: "globalNav.tools.aiCover", href: "/ai-cover" },
  { key: "globalNav.tools.vocalRemover", href: "/vocal-remover" },
  { key: "globalNav.tools.stemSplitter", href: "/stem-splitter" },
];
// Music creation workflows share the first navigation menu.
const CREATE = [
  { key: "globalNav.nav.createSong", href: "/ai-music-generator" },
  { key: "globalNav.nav.createMusicVideo", href: "/ai-music-video-generator" },
  { key: "globalNav.nav.createSingingVideo", href: "/ai-singing-video-generator" },
  {
    key: "globalNav.nav.createDanceVideo",
    href: "/ai-dance-video-generator",
    defaultValue: "Create Dance Video",
  },
  { key: "globalNav.nav.createLyricsVideo", href: "/lyrics-video-generator" },
];
const LINKS = [
  { key: "landingV2.header.myProjects", href: "/projects" },
  { key: "globalNav.nav.pricing", href: "/subscription" },
  { key: "globalNav.nav.blog", href: "/blog/" },
];

export default function HeaderV2({ onLogin, onJoin, onThemeToggle }: Props) {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { navigate, currentLang, changeLanguage } = useLanguageRouter();
  const { data: subscription } = trpc.payment.getSubscription.useQuery(undefined, {
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
  });
  const { data: unread = 0 } = trpc.notifications.unreadCount.useQuery(undefined, {
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30_000 : false,
  });
  const credits = subscription?.credits ?? null;
  const showUpgrade = isAuthenticated && getPlanTierIndex(subscription?.plan || "free") < 2;
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTab, setUpgradeTab] = useState<UpgradeModalTab>("yearly");
  const [menu, setMenu] = useState<"create" | "image" | "video" | "tools" | "more" | "me" | "rewards" | "lang" | null>(null);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: Event) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const go = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenu(null);
    if (href.startsWith("http")) window.open(href, "_blank", "noopener,noreferrer");
    else navigate(href);
  };
  const openUpgrade = (tab: UpgradeModalTab) => {
    setUpgradeTab(tab);
    setUpgradeOpen(true);
  };
  const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();

  return (
    <header data-enter="" style={{ "--d": "760ms" } as React.CSSProperties} ref={rootRef}>
      <div className="head-l">
        <a className="mark" href="/" onClick={go("/")}>
          Solmi<i>.</i>
        </a>
        <nav className="topnav" aria-label="Primary">
          <div
            className="drop hover"
            data-open={menu === "create"}
            onMouseEnter={() => setMenu("create")}
            onMouseLeave={() => setMenu((m) => (m === "create" ? null : m))}
          >
            <button
              type="button"
              className="navlink"
              aria-haspopup="true"
              aria-expanded={menu === "create"}
              onClick={() => setMenu((m) => (m === "create" ? null : "create"))}
            >
              {t("landingV2.header.musicVideo", { defaultValue: "Music Video" })} {chev}
            </button>
            <div className="menu" role="menu">
              {CREATE.map((item) => (
                <a key={item.href} role="menuitem" href={item.href} onClick={go(item.href)}>
                  {t(item.key, { defaultValue: (item as { defaultValue?: string }).defaultValue })}
                </a>
              ))}
            </div>
          </div>
          {(["image", "video"] as const).map((category) => (
            <div key={category} className="drop hover" data-open={menu === category}
              onMouseEnter={() => setMenu(category)}
              onMouseLeave={() => setMenu((m) => m === category ? null : m)}>
              <button type="button" className="navlink" aria-haspopup="true"
                aria-expanded={menu === category}
                onClick={() => setMenu((m) => (m === category ? null : category))}>
                {category === "image" ? t("landingV2.header.image", { defaultValue: "Image" }) : t("landingV2.header.video", { defaultValue: "Video" })} {chev}
              </button>
              <div className="menu creative-menu" role="menu" aria-label={category === "image" ? t("landingV2.header.imageTools", { defaultValue: "Image tools" }) : t("landingV2.header.videoTools", { defaultValue: "Video tools" })}>
                <div className="creative-menu-row" role="group" aria-label="Tools">
                  <div className="lbl">Tools</div>
                  <div className="creative-menu-links">
                    {creativeTools.filter(tool => creativeMenuCategories[category].includes(tool.category)).map(tool => (
                      <a key={tool.id} role="menuitem" href={creativeToolPath(tool)} onClick={go(creativeToolPath(tool))}><span className="creative-nav-icon">{category === "image" ? <ImageIcon size={21} /> : <VideoIcon size={21} />}</span><span className="creative-nav-copy"><strong>{tool.name}</strong><small>{tool.description}</small></span></a>
                    ))}
                    <a role="menuitem" href={`/${category}-tools`} onClick={go(`/${category}-tools`)}>Explore all →</a>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div
            className="drop hover"
            data-open={menu === "tools"}
            onMouseEnter={() => setMenu("tools")}
            onMouseLeave={() => setMenu((m) => (m === "tools" ? null : m))}
          >
            <button
              type="button"
              className="navlink"
              aria-haspopup="true"
              aria-expanded={menu === "tools"}
              onClick={() => setMenu((m) => (m === "tools" ? null : "tools"))}
            >
              {t("globalNav.nav.tools")} {chev}
            </button>
            <div className="menu" role="menu">
              {TOOLS.map((item) => (
                <a key={item.href} role="menuitem" href={item.href} onClick={go(item.href)}>
                  {t(item.key, { defaultValue: (item as { defaultValue?: string }).defaultValue })}
                </a>
              ))}
            </div>
          </div>
          {LINKS.map((l) => (
            <a key={l.href} className="navlink" href={l.href} onClick={go(l.href)}>
              {t(l.key)}
            </a>
          ))}
        </nav>
      </div>

      <div className="head-r">
        {/* Phones: every destination lives behind one button. */}
        <div className="drop navmore" data-open={menu === "more"}>
          <button
            type="button"
            className="iconbtn"
            aria-label={t("landingV2.header.menu")}
            aria-haspopup="true"
            aria-expanded={menu === "more"}
            onClick={() => setMenu((m) => (m === "more" ? null : "more"))}
          >
            {dots}
          </button>
          <div className="menu right" role="menu">
            <div className="lbl">{t("landingV2.header.musicVideo", { defaultValue: "Music Video" })}</div>
            {CREATE.map((l) => <a key={l.key} role="menuitem" href={l.href} onClick={go(l.href)}>{t(l.key)}</a>)}
            <a role="menuitem" href="/image-tools" onClick={go("/image-tools")}>{t("landingV2.header.imageTools", { defaultValue: "Image tools" })}</a>
            <a role="menuitem" href="/video-tools" onClick={go("/video-tools")}>{t("landingV2.header.videoTools", { defaultValue: "Video tools" })}</a>
            {[...LINKS, ...TOOLS].map((l) => (
              <a key={l.key} role="menuitem" href={l.href} onClick={go(l.href)}>
                {t(l.key)}
              </a>
            ))}
            <a role="menuitem" href={DISCORD_INVITE_URL} onClick={go(DISCORD_INVITE_URL)}>{t("landingV2.header.discord")}</a>
            {/* The language pill is hidden on phones (no room next to the
                wordmark), so the phone menu hands off to the language list. */}
            <button type="button" role="menuitem" className="langrow" onClick={() => setMenu("lang")}>
              {t("landingV2.header.language")} <span>{currentLang.toUpperCase()}</span>
            </button>
          </div>
        </div>

        <button onClick={onThemeToggle} className="theme" id="theme" aria-label={t("landingV2.header.theme")} title={t("landingV2.header.theme")}>
          <svg className="sun" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
          <svg className="moon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
        </button>

        <a className="pill discord icon" href={DISCORD_INVITE_URL} onClick={go(DISCORD_INVITE_URL)} title={t("landingV2.header.discord")} aria-label={t("landingV2.header.discord")}>
          {discord}
        </a>

        {/* Language: globe + current code; the menu lists every locale by its
            native name and routes to the same page under the new prefix. */}
        <div className="drop langdrop" data-open={menu === "lang"}>
          <button
            type="button"
            className="pill lang"
            title={t("landingV2.header.language")}
            aria-label={t("landingV2.header.language")}
            aria-haspopup="true"
            aria-expanded={menu === "lang"}
            onClick={() => setMenu((m) => (m === "lang" ? null : "lang"))}
          >
            {globe} <span className="long">{currentLang.toUpperCase()}</span>
          </button>
          <div className="menu right lang" role="menu">
            {languageNames.map(({ code, name }) => (
              <button
                key={code}
                type="button"
                role="menuitemradio"
                aria-checked={code === currentLang}
                onClick={() => {
                  setMenu(null);
                  if (code !== currentLang) changeLanguage(code);
                }}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {isAuthenticated && user ? (
          <>
            {showUpgrade && (
              <button type="button" className="upgrade" onClick={() => openUpgrade("yearly")}>
                {crown} {t("globalNav.nav.upgrade")}
              </button>
            )}
            <div
              className="drop hover"
              data-open={menu === "rewards"}
              onMouseEnter={() => setMenu("rewards")}
              onMouseLeave={() => setMenu((m) => (m === "rewards" ? null : m))}
            >
              <button
                type="button"
                className="pill"
                aria-haspopup="true"
                aria-expanded={menu === "rewards"}
                onClick={() => setMenu((m) => (m === "rewards" ? null : "rewards"))}
              >
                {trophy} <span className="long">{t("landingV2.header.rewards")}</span>
              </button>
              <div className="menu right" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenu(null); setRewardsOpen(true); }}>
                  <span className="mi">{shareIcon} {t("landingV2.header.shareEarn")}</span>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenu(null); setInviteOpen(true); }}>
                  <span className="mi">{giftIcon} {t("landingV2.header.inviteEarn")}</span>
                </button>
                <a role="menuitem" href="/affiliate-program" onClick={go("/affiliate-program")}>
                  <span className="mi">{handshakeIcon} {t("landingV2.header.affiliate")}</span>
                </a>
              </div>
            </div>
            <button
              type="button"
              className="pill credits"
              title={t("globalNav.nav.creditsTitle")}
              onClick={() => openUpgrade("topup")}
            >
              {star} {(credits ?? 0).toLocaleString()}
            </button>
            <button
              type="button"
              className="iconbtn bell"
              aria-label={`${t("globalNav.nav.notifications")}${unread ? ` (${unread})` : ""}`}
              onClick={() => navigate("/notifications")}
            >
              {bell}
              {unread > 0 && <span className="badge">{unread > 99 ? "99+" : unread}</span>}
            </button>
            <div className="drop" data-open={menu === "me"}>
              <button
                type="button"
                className="avatar"
                aria-label={t("landingV2.header.account")}
                aria-haspopup="true"
                aria-expanded={menu === "me"}
                onClick={() => setMenu((m) => (m === "me" ? null : "me"))}
              >
                {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initial}
              </button>
              <div className="menu right" role="menu">
                <div className="who">
                  <b>{user.name || t("landingV2.header.member")}</b>
                  {user.email && <span>{user.email}</span>}
                </div>
                <a role="menuitem" href="/projects" onClick={go("/projects")}>{t("landingV2.header.myProjectsMenu")}</a>
                <a role="menuitem" href={`/author/${user.id}`} onClick={go(`/author/${user.id}`)}>{t("globalNav.nav.myProfile")}</a>
                <a role="menuitem" href="/subscription" onClick={go("/subscription")}>{t("globalNav.nav.manageSubscription")}</a>
                <button
                  type="button"
                  role="menuitem"
                  onClick={async () => {
                    setMenu(null);
                    await logout();
                    window.location.reload();
                  }}
                >
                  {t("globalNav.nav.logout")}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <a className="login" href="#" onClick={(e) => { e.preventDefault(); onLogin(); }}>
              {t("globalNav.nav.signIn")}
            </a>
            <button className="join" onClick={onJoin}>
              <span className="long">{t("landingV2.header.join")}</span>
              <span className="short">{t("landingV2.header.joinShort")}</span>
            </button>
          </>
        )}
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        trigger="landing_nav"
        defaultTab={upgradeTab}
      />
      {isAuthenticated && (
        <>
          <InviteFriendsDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
          {/* Share-to-earn hub without a project: submit stays gated until a
              video is exported, same as the current header. */}
          <RewardsBanner projectId={null} hideTrigger open={rewardsOpen} onOpenChange={setRewardsOpen} />
        </>
      )}
    </header>
  );
}

const svg = (d: ReactNode, size = 14, fill = "none") => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const chev = <svg className="chev" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>;
const dots = svg(<><circle cx="5" cy="12" r="1.5" fill="currentColor" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><circle cx="19" cy="12" r="1.5" fill="currentColor" /></>, 16);
const crown = svg(<path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z" />, 13);
const star = svg(<path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9z" />, 13, "currentColor");
const trophy = svg(<><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 21h8M9 17h6" /></>, 14);
const shareIcon = svg(<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></>, 16);
const giftIcon = svg(<><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13M5 12v9h14v-9" /><path d="M12 8c-1.5-3-6-3-6-.5S10 8 12 8zm0 0c1.5-3 6-3 6-.5S14 8 12 8z" /></>, 16);
const handshakeIcon = svg(<><path d="M11 17l2 2a2 2 0 0 0 3-3l-4-4M13 8l-2-2a3 3 0 0 0-4 0L3 10l3 3a2 2 0 0 0 3 0" /><path d="M11 6l4-1 3 1 3 4-3 3-2-2" /></>, 16);
const folder = svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />, 14);
const globe = svg(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>, 14);
const bell = svg(<><path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" /><path d="M10 20a2 2 0 0 0 4 0" /></>, 16);
const discord = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.6 5.4A16 16 0 0 0 15.7 4l-.2.4a15 15 0 0 1 3.6 1.8 13 13 0 0 0-14.2 0A15 15 0 0 1 8.5 4.4L8.3 4a16 16 0 0 0-3.9 1.4C1.9 9.1 1.2 12.7 1.5 16.2A16 16 0 0 0 6.4 18.7l1-1.4a10 10 0 0 1-1.7-.8l.4-.3a11.5 11.5 0 0 0 11.8 0l.4.3a10 10 0 0 1-1.7.8l1 1.4a16 16 0 0 0 4.9-2.5c.4-4-.7-7.6-2.9-10.8zM8.7 14.1c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2zm6.6 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2z" />
  </svg>
);
